import i18n from "../../i18n/i18n";
import { getCurrentCompanieId } from "../../utils/currentCompanie";
import { Permission } from "../../auth/permissions";

type TypeItem = {
  label: string;
  iconName: string;
  width: number;
  height: number;
  to: string;
  /**
   * Capability needed to see this entry. Omit for entries everyone may see.
   * Must match the permission on the corresponding <PrivateRoute>, otherwise
   * the menu advertises a destination that renders "Acces refuse".
   */
  permission?: Permission;
  /** Holding ANY of these shows the entry. Use for aggregate screens. */
  anyOf?: Permission[];
  /**
   * Which of the sidebar's two visual groups the entry belongs to.
   *
   * Sidebar.tsx used to split this array with `slice(0, 9)` / `slice(9)`. That
   * index is invalidated by inserting, removing OR permission-filtering any
   * entry, which silently moves unrelated items across the divider. Declaring
   * the group on the item makes the split independent of array position.
   */
  group?: "main" | "bottom";
};

const items = [] as TypeItem[];

items.push({
  label: i18n.t("sidebar.dashboard", "Dashboard"),
  iconName: "layout",
  width: 24,
  height: 24,
  to: "/dashboard",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.admin", "Admin"),
  iconName: "lockSquareRounded",
  width: 24,
  height: 24,
  to: "/admin",
  permission: "fleet.user.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.users", "Users"),
  iconName: "userCircle",
  width: 24,
  height: 24,
  to: `/users`,
  permission: "fleet.user.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.department", "Department"),
  iconName: "stars",
  width: 24,
  height: 24,
  to: `/compagnies/${getCurrentCompanieId()}/departments`,
  permission: "fleet.company.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.cars", "Cars"),
  iconName: "car",
  width: 24,
  height: 24,
  to: "/cars",
  permission: "fleet.vehicle.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.brands", "Brands"),
  iconName: "steeringWheel",
  width: 24,
  height: 24,
  to: "/brands",
  permission: "catalogue.brand.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.compagnies", "Compagnies"),
  iconName: "buildingSkyscraper",
  width: 24,
  height: 24,
  to: "/compagnies",
  permission: "fleet.company.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.services", "Services"),
  iconName: "servicemark", 
  width: 24,
  height: 24,
  to: "/services",
  permission: "catalogue.service.manage",
  group: "main",
});

items.push({
  label: i18n.t("sidebar.signs", "Signs"),
  iconName: "buildingStore", 
  width: 24,
  height: 24,
  to: "/signs",
  permission: "catalogue.sign.manage",
  group: "main",
});

/*
 * Centres & Stations — the ingestion pipeline's operator surface.
 *
 * Deliberately gated on catalogue.sign.manage, the same permission as the
 * matching <PrivateRoute>. Inventing a `catalogue.poi.manage` here would
 * silently hide the entry from every existing role until the permission is
 * seeded, and the menu comment above already warns that a mismatch advertises
 * a destination that renders "Acces refuse".
 */
items.push({
  label: i18n.t("sidebar.poi", "Centres & Stations"),
  iconName: "map2",
  width: 24,
  height: 24,
  to: "/poi",
  permission: "catalogue.sign.manage",
  group: "main",
});

// NEW: Pub menu item
items.push({
  label: i18n.t("sidebar.pub", "Pub"),
  iconName: "broadcast", // or "speaker", "broadcast", "volume"
  width: 24,
  height: 24,
  to: "/pubs",
  permission: "catalogue.pub.manage",
  group: "bottom",
});

items.push({
  label: i18n.t("sidebar.billing", "Paiements"),
  iconName: "coinEuro",
  width: 24,
  height: 24,
  to: "/billing",
  // The billing screen aggregates settings, pricing and promo codes; a role
  // holding any one of them has something to do there.
  anyOf: [
    "billing.plan.view",
    "billing.pricing.view",
    "billing.promo.view",
    "billing.settings.view",
  ],
  group: "bottom",
});

items.push({
  label: i18n.t("sidebar.parametres", "Parameters"),
  iconName: "settings", 
  width: 24,
  height: 24,
  to: "/parametres",
  permission: "platform.parameters.manage",
  group: "bottom",
});

items.push({
  label: i18n.t("sidebar.retranscription", "Retranscription"),
  iconName: "arrowLeftRight",
  width: 24,
  height: 24,
  to: "/retranscription",
  permission: "platform.retranscription.view",
  group: "bottom",
});

/*
 * Journaux — audit trail + system logs.
 *
 * Gated on platform.audit.view, matching the <PrivateRoute> and the API. The
 * comment on TypeItem.permission above warns that a mismatch advertises a
 * destination that renders "Acces refuse"; this key is new, so it must be
 * seeded before the entry appears for anyone — run `permissions:sync` (or the
 * PermissionSeeder) on deploy. Super-admins hold it implicitly via the
 * wildcard and will see it immediately.
 *
 * In the bottom group with the other platform-wide, non-daily screens.
 */
items.push({
  label: i18n.t("sidebar.audit", "Journaux"),
  iconName: "history",
  width: 24,
  height: 24,
  to: "/journaux",
  permission: "platform.audit.view",
  group: "bottom",
});

/*
 * Accueil — sante du parcours d'entree des clients.
 *
 * Gardee sur `fleet.user.manage`, la meme clef que le <PrivateRoute> et que
 * l'API. Contrairement a l'entree Journaux ci-dessous, cette clef est DEJA
 * semee (elle protege Utilisateurs et Societes), donc l'entree apparait des le
 * deploiement sans passer par permissions:sync.
 *
 * Dans le groupe bas avec les autres ecrans transverses, non quotidiens : on
 * l'ouvre quand un client signale un probleme, ou le matin pour verifier que
 * les quatre compteurs sont a zero.
 */
items.push({
  label: i18n.t("sidebar.onboarding", "Accueil"),
  iconName: "userCircle",
  width: 24,
  height: 24,
  to: "/accueil",
  permission: "fleet.user.manage",
  group: "bottom",
});

items.push({
  label: i18n.t("sidebar.help", "Help"),
  iconName: "help",
  width: 24,
  height: 24,
  to: "",
  group: "bottom",
});

export default items;